import {useMemo, useState, type ReactNode} from "react";
import type {CurrencyEnum} from "../../../../../../generated/enums.ts";
import type {DocumentSettlement} from "../../../types/types.ts";
import {showsPaymentStatus} from "../../constants.ts";
import type {PanelBooking} from "../../types.ts";
import {
    buildVatRows,
    sortVatRows,
    summarizeDocuments,
    toggleVatRowSort,
    type VatRowSort,
    type VatRowSortColumn,
} from "../../utils/invoiceVatRows.ts";
import {InvoiceVatGroupsTable} from "./InvoiceVatGroupsTable.tsx";
import type {SelectAllState} from "./VatTableParts.tsx";

const DATE_SORT: VatRowSort = {column: "date", ascending: true};

interface InvoiceVatViewProps {
    groupByRelation: boolean;
    selectAll: SelectAllState;
    bookings: PanelBooking[];
    accountCode: string;
    currency: CurrencyEnum;
    documentSettlements: DocumentSettlement[];
    renderBookings: (bookings: PanelBooking[]) => ReactNode;
}

export function InvoiceVatView({
    groupByRelation,
    selectAll,
    bookings,
    accountCode,
    currency,
    documentSettlements,
    renderBookings,
}: InvoiceVatViewProps) {
    const [sort, setSort] = useState<VatRowSort>(DATE_SORT);
    const [expandedKeys, setExpandedKeys] = useState<Set<string>>(new Set());

    const documents = useMemo(
        () => sortVatRows(summarizeDocuments(buildVatRows(bookings, currency), currency), sort),
        [bookings, currency, sort],
    );
    const settlementByBill = useMemo(() => {
        const byBill = new Map<string, DocumentSettlement>();
        for (const settlement of documentSettlements) {
            for (const billId of settlement.bill_ids) byBill.set(billId, settlement);
        }
        return byBill;
    }, [documentSettlements]);

    function toggleRow(key: string) {
        setExpandedKeys((current) => {
            const next = new Set(current);
            if (next.has(key)) next.delete(key);
            else next.add(key);
            return next;
        });
    }

    if (documents.length === 0) {
        return <div className="px-4 py-8 text-center text-sm text-muted-foreground">Geen boekingen voor dit filter.</div>;
    }

    return (
        <InvoiceVatGroupsTable
            documents={documents}
            groupByRelation={groupByRelation}
            selectAll={selectAll}
            currency={currency}
            sort={sort}
            onSortChange={(column: VatRowSortColumn) => setSort((current) => toggleVatRowSort(current, column))}
            expandedKeys={expandedKeys}
            onToggleRow={toggleRow}
            showStatus={showsPaymentStatus(accountCode)}
            settlementFor={(billId) => (billId ? settlementByBill.get(billId) ?? null : null)}
            renderBookings={renderBookings}
        />
    );
}
